import type { ReactElement } from 'react';
import { Activity, BadgeDollarSign, CircleGauge, ExternalLink, Layers3, Radar, Star, Waves } from 'lucide-react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Text,
  chakra,
} from '@chakra-ui/react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { Quake } from '../api/types';
import type { PriceSample } from '../hooks/useLivePriceSamples';
import { money, pctFromProb, pp, spreadPct } from '../lib/format';
import { polymarketEventUrlFromEventSlug } from '../lib/polymarket';
import { ScoreGauge } from './ScoreGauge';

export type FeaturedWindow = '1m' | '5m' | '15m' | '1h' | '1D';
type SupportedFeaturedWindow = Exclude<FeaturedWindow, '1D'>;

const supportedWindows: readonly SupportedFeaturedWindow[] = ['1m', '5m', '15m', '1h'];
const allWindows: readonly FeaturedWindow[] = ['1m', '5m', '15m', '1h', '1D'];
const windowDurations: Record<SupportedFeaturedWindow, number> = {
  '1m': 60_000,
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
};

export function FeaturedMarket({
  mover: featuredProp,
  selectedWindow,
  onWindowChange,
  onUseTopMover,
  samples,
  watchlistFallback,
}: {
  mover: Quake | undefined;
  selectedWindow: FeaturedWindow;
  onWindowChange: (window: SupportedFeaturedWindow) => void;
  onUseTopMover: () => void;
  samples: readonly PriceSample[];
  /** When the user's current-mode mover set is empty, fall back to the
   *  top watchlist near-miss so the Featured Market panel never goes blank
   *  when there is any signal in either band. */
  watchlistFallback?: readonly Quake[] | undefined;
}) {
  // Pick the highest-heat fallback from the watchlist band if the
  // user's current mode produced no mover. The badge in the header
  // tells the user this is a near-miss, not a confirmed signal.
  const featured =
    featuredProp ?? (watchlistFallback && watchlistFallback.length > 0 ? watchlistFallback[0] : undefined);
  const fromFallback = !featuredProp && Boolean(featured);

  if (!featured) {
    return (
      <FeaturedMarketEmptyState
        selectedWindow={selectedWindow}
        onWindowChange={onWindowChange}
        onUseTopMover={onUseTopMover}
      />
    );
  }

  const positive = featured.change_pp >= 0;
  const chartData = chartPoints(samples, selectedWindow);
  const yDomain = chartDomain(chartData);
  const marketURL = polymarketEventUrlFromEventSlug(featured.event_slug);

  return (
    <Box as="section" layerStyle="glassPanel" minH="520px" overflow="hidden" borderRadius="oqPanel">
      <FeaturedMarketHeader selectedWindow={selectedWindow} onWindowChange={onWindowChange} onUseTopMover={onUseTopMover} fromFallback={fromFallback} />

      <Grid gap="5" p="5" templateColumns={{ base: '1fr', lg: 'minmax(180px,.32fr) minmax(360px,1fr) minmax(260px,.46fr)' }}>
        <Stack gap="0">
          <Flex wrap="wrap" align="center" gap="3">
            <Heading as="h3" size={{ base: 'xl', md: '2xl' }} fontWeight="semibold" lineHeight="tight" letterSpacing="tight">
              {featured.question}
            </Heading>
            <Box
              display="inline-block"
              maxW="40"
              truncate
              whiteSpace="nowrap"
              borderRadius="md"
              px="3"
              py="1"
              alignSelf="center"
              fontSize="sm"
              fontWeight="black"
              textTransform="uppercase"
              {...(positive
                ? { bg: 'signal.good/14', color: 'signal.good' }
                : { bg: 'signal.bad/14', color: 'signal.bad' })}
              title={featured.outcome}
            >
              {featured.outcome}
            </Box>
            <Box
              borderRadius="md"
              borderWidth="1px"
              borderColor="signal.bad/25"
              bg="signal.bad/10"
              px="3"
              py="1"
              fontSize="sm"
              fontWeight="semibold"
              color="signal.bad"
            >
              {featured.score >= 80 ? 'High-confidence quake' : 'Live quake'}
            </Box>
            {marketURL ? (
              <Link
                href={marketURL}
                target="_blank"
                rel="noreferrer noopener"
                display="inline-flex"
                alignItems="center"
                gap="1.5"
                borderRadius="md"
                borderWidth="1px"
                borderColor="signal.blue/20"
                bg="signal.blue/10"
                px="3"
                py="1"
                fontSize="sm"
                fontWeight="semibold"
                color="brand.fg"
                transition="all 0.2s"
                _hover={{ color: 'text.primary' }}
              >
                Polymarket <ExternalLink size={14} />
              </Link>
            ) : null}
          </Flex>

          <Text mt="7" fontSize="sm" color="text.muted">
            Live Price
          </Text>
          <Text mt="1" fontSize="6xl" fontWeight="semibold" letterSpacing="tight" color="text.primary">
            {pctFromProb(featured.new_price)}
          </Text>
          <Text mt="2" fontSize="lg" fontWeight="bold" color={positive ? 'signal.good' : 'signal.bad'}>
            {pp(featured.change_pp)} ({((featured.new_price / Math.max(featured.old_price, 0.01) - 1) * 100).toFixed(1)}%)
          </Text>
          <Text mt="4" fontSize="sm" color="text.muted">
            {pctFromProb(featured.old_price)} {featured.window} ago
          </Text>
        </Stack>

        <Box className="soft-grid" minH="240px" borderRadius="oqPanel" borderWidth="1px" borderColor="border.subtle" bg="blackAlpha.100" p="3">
          {chartData.length >= 2 && yDomain ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="featuredPriceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={positive ? 'var(--oq-colors-signal-good)' : 'var(--oq-colors-signal-bad)'} stopOpacity={0.42} />
                    <stop offset="95%" stopColor={positive ? 'var(--oq-colors-signal-good)' : 'var(--oq-colors-signal-bad)'} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--oq-colors-border-subtle)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'var(--oq-colors-text-muted)', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={22}
                />
                <YAxis
                  domain={yDomain}
                  tickFormatter={(value) => `${Math.round(Number(value) * 100)}%`}
                  tick={{ fill: 'var(--oq-colors-text-muted)', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={42}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--oq-colors-surface-panel)',
                    border: '1px solid var(--oq-colors-border-subtle)',
                    borderRadius: '12px',
                    color: 'var(--oq-colors-text-primary)',
                  }}
                  formatter={(value) => [`${Math.round(Number(value) * 100)}%`, 'Price']}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={positive ? 'var(--oq-colors-signal-good)' : 'var(--oq-colors-signal-bad)'}
                  strokeWidth={3}
                  fill="url(#featuredPriceGradient)"
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmptyState selectedWindow={selectedWindow} />
          )}
        </Box>

        <Box borderRadius="oqPanel" borderWidth="1px" borderColor="border.subtle" bg="blackAlpha.100" p="4">
          <StatLine icon={<CircleGauge />} label="Velocity" value={pp(featured.velocity_pps * featured.window_seconds)} tone={positive ? 'good' : 'bad'} />
          <StatLine icon={<BadgeDollarSign />} label="Window Vol" value={money(featured.window_volume)} />
          <StatLine icon={<BadgeDollarSign />} label="Baseline" value={money(featured.baseline_volume)} />
          <StatLine
            icon={<Waves />}
            label="Spike"
            value={ratio(featured.volume_spike_ratio)}
            {...(featured.volume_spike_ratio >= 2 ? { tone: 'good' as const } : {})}
          />
          <StatLine icon={<Activity />} label="Capital Move" value={`${money(featured.capital_weighted_move)}pp`} />
          <StatLine
            icon={<Activity />}
            label="Persistence"
            value={ratio(featured.directional_persistence)}
            tone={featured.directional_persistence >= 1 ? 'good' : 'bad'}
          />
          <StatLine
            icon={<Radar />}
            label="Bid / Ask"
            value={`${Math.round((featured.new_price - featured.spread / 2) * 100)}% / ${Math.round((featured.new_price + featured.spread / 2) * 100)}%`}
          />
          <StatLine icon={<CircleGauge />} label="Spread" value={spreadPct(featured.spread)} />
          <StatLine icon={<Layers3 />} label="Market Depth" value={money(featured.depth_near_mid)} />
          <StatLine icon={<Waves />} label="Liquidity" value={featured.depth_near_mid > 500_000 ? 'High' : 'Medium'} tone="good" />
          <Flex mt="5" align="center" justify="space-between" borderTopWidth="1px" borderColor="border.subtle" pt="4">
            <Flex align="center" gap="2" fontSize="sm" color="text.muted">
              <Activity size={16} />
              Quake Score
            </Flex>
            <Flex align="center" gap="3">
              <ScoreGauge score={featured.score} />
              <Text fontSize="lg" fontWeight="semibold" color="text.muted">
                /100
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Grid>
    </Box>
  );
}

function FeaturedMarketHeader({
  selectedWindow,
  onWindowChange,
  onUseTopMover,
  fromFallback,
}: {
  selectedWindow: FeaturedWindow;
  onWindowChange: (window: SupportedFeaturedWindow) => void;
  onUseTopMover: () => void;
  fromFallback?: boolean;
}) {
  return (
    <Flex align="center" justify="space-between" borderBottomWidth="1px" borderColor="border.subtle" px="5" py="3">
      <Flex align="center" gap="3">
        {fromFallback ? (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="1.5"
            borderRadius="oqControl"
            borderWidth="1px"
            borderColor="signal.warn/30"
            bg="signal.warn/10"
            px="2"
            py="0.5"
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
            color="signal.warn"
            title="No confirmed mover right now — showing the top watchlist near-miss as a fallback."
          >
            Near miss
          </Box>
        ) : null}
        <Box display="grid" placeItems="center" h="6" w="6" borderRadius="md" bg="signal.blue" fontSize="sm" fontWeight="bold">
          3
        </Box>
        <Heading as="h2" size="lg" fontWeight="semibold">
          Featured Market
        </Heading>
        <Star size={16} color="var(--oq-colors-signal-warn)" fill="var(--oq-colors-signal-warn)" />
      </Flex>
      <Flex align="center" gap="2">
        <chakra.button
          type="button"
          onClick={onUseTopMover}
          display={{ base: 'none', md: 'inline-flex' }}
          alignItems="center"
          gap="1.5"
          borderRadius="oqControl"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.100"
          px="3"
          py="1.5"
          fontSize="xs"
          fontWeight="semibold"
          color="text.muted"
          transition="all 0.2s"
          _hover={{ color: 'text.primary', borderColor: 'signal.blue/30' }}
        >
          Use top mover
        </chakra.button>
        <HStack
          gap="0"
          overflow="hidden"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.100"
          display={{ base: 'none', md: 'flex' }}
        >
          {allWindows.map((label) => {
            const active = selectedWindow === label;
            const disabled = label === '1D';
            return (
              <chakra.button
                key={label}
                type="button"
                disabled={disabled}
                title={disabled ? '1D requires historical price persistence.' : `${label} live sample window`}
                aria-pressed={active}
                onClick={() => {
                  if (isSupportedWindow(label)) onWindowChange(label);
                }}
                px="3"
                py="1.5"
                fontSize="xs"
                fontWeight="semibold"
                color={active ? 'text.primary' : 'text.muted'}
                bg={active ? 'signal.blue/20' : 'transparent'}
                cursor={disabled ? 'not-allowed' : 'pointer'}
                opacity={disabled ? 0.45 : 1}
                transition="all 0.2s"
                _hover={{ color: 'text.primary', bg: active ? 'signal.blue/25' : 'whiteAlpha.50' }}
                _disabled={{ cursor: 'not-allowed', opacity: 0.45, _hover: {} }}
              >
                {label}
              </chakra.button>
            );
          })}
        </HStack>
      </Flex>
    </Flex>
  );
}

function FeaturedMarketEmptyState({
  selectedWindow,
  onWindowChange,
  onUseTopMover,
}: {
  selectedWindow: FeaturedWindow;
  onWindowChange: (window: SupportedFeaturedWindow) => void;
  onUseTopMover: () => void;
}) {
  return (
    <Box as="section" layerStyle="glassPanel" minH="520px" overflow="hidden" borderRadius="oqPanel">
      <FeaturedMarketHeader selectedWindow={selectedWindow} onWindowChange={onWindowChange} onUseTopMover={onUseTopMover} />
      <Box display="grid" minH="360px" placeItems="center" px="5" py="10">
        <Box maxW="md" textAlign="center">
          <Box mx="auto" display="grid" h="14" w="14" placeItems="center" borderRadius="oqPanel" borderWidth="1px" borderColor="border.subtle" bg="black/20" color="brand.fg">
            <Activity size={28} />
          </Box>
          <Heading as="h3" mt="5" size="lg" fontWeight="semibold" color="text.primary">
            No featured market right now
          </Heading>
          <Text mt="2" fontSize="sm" lineHeight="6" color="text.muted">
            Oddsquake is connected, but no market currently clears the active mover filters.
          </Text>
          <SimpleGrid mt="6" columns={3} gap="2" textAlign="left" fontSize="xs" color="text.muted">
            <Box borderRadius="lg" borderWidth="1px" borderColor="border.subtle" bg="black/10" p="3">
              <Text fontWeight="semibold" color="text.primary">
                Move
              </Text>
              <Text mt="1">Waiting</Text>
            </Box>
            <Box borderRadius="lg" borderWidth="1px" borderColor="border.subtle" bg="black/10" p="3">
              <Text fontWeight="semibold" color="text.primary">
                Liquidity
              </Text>
              <Text mt="1">Filtered</Text>
            </Box>
            <Box borderRadius="lg" borderWidth="1px" borderColor="border.subtle" bg="black/10" p="3">
              <Text fontWeight="semibold" color="text.primary">
                Score
              </Text>
              <Text mt="1">Idle</Text>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>
    </Box>
  );
}

function ChartEmptyState({ selectedWindow }: { selectedWindow: FeaturedWindow }) {
  const message =
    selectedWindow === '1D'
      ? '1D requires historical price persistence.'
      : `Collecting live ${selectedWindow} samples while this page is open.`;

  return (
    <Box display="grid" h="260px" placeItems="center" px="5" textAlign="center">
      <Box maxW="sm">
        <Box mx="auto" display="grid" h="12" w="12" placeItems="center" borderRadius="oqPanel" borderWidth="1px" borderColor="border.subtle" bg="black/20" color="brand.fg">
          <Activity size={24} />
        </Box>
        <Heading as="h3" mt="4" fontSize="base" fontWeight="semibold" color="text.primary">
          Insufficient live samples
        </Heading>
        <Text mt="2" fontSize="sm" lineHeight="6" color="text.muted">
          {message}
        </Text>
      </Box>
    </Box>
  );
}

function StatLine({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactElement;
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <Flex align="center" justify="space-between" borderBottomWidth="1px" borderColor="border.subtle" py="2.5" _last={{ borderBottomWidth: 0 }}>
      <Flex align="center" gap="2" fontSize="sm" color="text.muted">
        <Box css={{ '& > svg': { height: '4', width: '4' } }}>{icon}</Box>
        {label}
      </Flex>
      <Text fontWeight="semibold" color={tone === 'good' ? 'signal.good' : tone === 'bad' ? 'signal.bad' : 'text.primary'}>
        {value}
      </Text>
    </Flex>
  );
}

function isSupportedWindow(value: FeaturedWindow): value is SupportedFeaturedWindow {
  return supportedWindows.includes(value as SupportedFeaturedWindow);
}

interface ChartPoint {
  label: string;
  price: number;
}

function chartPoints(samples: readonly PriceSample[], selectedWindow: FeaturedWindow): ChartPoint[] {
  if (!isSupportedWindow(selectedWindow)) return [];
  const now = Date.now();
  const cutoff = now - windowDurations[selectedWindow];
  return samples
    .filter((sample) => sample.at >= cutoff)
    .sort((left, right) => left.at - right.at)
    .map((sample) => ({
      label: new Date(sample.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      price: sample.price,
    }));
}

function chartDomain(points: readonly ChartPoint[]): [number, number] | null {
  if (points.length < 2) return null;
  const values = points.map((point) => point.price);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const padding = Math.max(0.01, (maxValue - minValue) * 0.2);
  return [Math.max(0, minValue - padding), Math.min(1, maxValue + padding)];
}

function ratio(value: number) {
  return `${value.toFixed(value >= 10 ? 0 : 2)}x`;
}